import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/home");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--bg-base)]">
      <div className="flex items-center justify-center p-8 fade-up">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2.5 mb-10">
            <img
              src="/logo-talmia-ii.svg"
              alt="Talmia II"
              className="w-14 h-14 rounded-xl object-cover"
            />
            <span className="font-display text-xl font-bold">Talmia<span className="text-orange-400">II</span></span>
          </Link>

          <h1 className="font-display text-4xl sm:text-5xl font-extrabold mb-2 tracking-tight">Welcome back</h1>
          <p className="text-slate-400 mb-8">Sign in to access Talmia functions.</p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs font-medium text-slate-300 uppercase tracking-wider">Email</label>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="input-dark mt-1.5" placeholder="you@company.com" data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <label className="text-xs font-medium text-slate-300 uppercase tracking-wider">Password</label>
                <Link to="/forgot-password" className="text-xs text-orange-400 hover:text-orange-300 font-medium" data-testid="link-to-forgot">
                  Forgot?
                </Link>
              </div>
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="input-dark mt-1.5" placeholder="••••••••" data-testid="login-password-input"
              />
            </div>
            {error && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
            <button type="submit" disabled={loading} className="btn-coral w-full mt-2" data-testid="login-submit-button">
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <p className="mt-6 text-sm text-slate-400">
            New to Talmia? <Link to="/register" className="text-orange-400 hover:text-orange-300 font-medium" data-testid="link-to-register">Create an account</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/40 via-slate-900 to-orange-900/30" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 30% 20%, rgba(255,104,32,0.28) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(90,79,207,0.30) 0%, transparent 50%)" }} />
        <div className="relative h-full flex flex-col justify-end p-12">
          <div className="font-display text-3xl font-bold leading-tight max-w-md">
            Sales, CRM, Ops. <span className="text-orange-400">One workspace.</span>
          </div>
          <p className="text-slate-400 mt-3 max-w-md">
            The unified command centre for the Talmia team — deals, leads, quotes,
            calls and knowledge in one place.
          </p>
        </div>
      </div>
    </div>
  );
}
